import {useState, useCallback, memo, useEffect, useContext} from 'react';

import {get, debounce} from 'lodash';
import styled from 'styled-components';

import {body16Normal} from '~/shared/theme/typography';
import {flexColumn} from '~/shared/theme/FlexLayout';
import {getLocalizationService} from '~/shared/services/localisationService';
import {AddressViewValuesMap} from '~/shared/consts/addressConsts';

import {AddressDropdownContext} from '../context';
import {BlockHeader, TitleText, BackIcon, AddressDropdownButton as BackButton} from '../styled.addressDropdown';

import FormWrapper from './FormWrapper';

const TitlesContainer = styled.div`
  ${flexColumn};
  flex: 1;
`;

const SubTitleText = styled.label.attrs({
  tabIndex: 0,
})`
  width: 100%;
  flex: 1;
  color: ${({theme}) => theme.addressDropdown.form.subTitle};
  ${body16Normal};
  line-height: 16px;
  text-align: center;
  padding-top: 5px;
  max-height: 22px;
  overflow-y: hidden;
`;

const WAIT_MS = 700;

const EditRemoteAddress = ({onViewChange, editedAddress, addresses, editing}) => {
  const {t} = getLocalizationService();
  const {cityNameAutocomplete, streetNameAutocomplete, fetchCityNames, fetchStreetNames, onSubmit, deleteAddress} =
    useContext(AddressDropdownContext);

  const titleRef = useCallback(titleNode => {
    titleNode?.focus();
  }, []);

  const [isCommentsExpended, setIsCommentsExpended] = useState(Boolean(editedAddress.comments));
  const [disabledDelete, setDisabledDelete] = useState(false);

  useEffect(() => {
    setDisabledDelete(disabledDelete || addresses.length < 2);
  }, [addresses.length, disabledDelete, setDisabledDelete]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchCityNamesIfNeeded = useCallback(
    debounce(cityNameInputValue => {
      if (!cityNameInputValue && !fetchCityNames) {
        return;
      }

      fetchCityNames(cityNameInputValue);
    }, WAIT_MS),
    [fetchCityNames],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchStreetNamesIfNeeded = useCallback(
    debounce((searchValue, cityId) => {
      if (!searchValue && !fetchStreetNames) {
        return;
      }

      fetchStreetNames({searchValue, parentId: cityId});
    }, WAIT_MS),
    [fetchStreetNames],
  );

  const handleDeleteButtonClick = e => {
    e.preventDefault();
    deleteAddress(addresses);
  };

  const getStreetNameAutocomplete = (cityId, streetName) => {
    return streetName ? (get(streetNameAutocomplete, cityId) || []).filter(({name}) => name.includes(streetName)) : [];
  };

  if (!editedAddress) {
    return null;
  }

  return (
    <>
      <BlockHeader>
        <TitlesContainer>
          <TitleText ref={titleRef}>{t('edit_address')}</TitleText>
          <SubTitleText>{`${editedAddress?.streetName} ${editedAddress?.houseNumber}, ${editedAddress?.cityName}`}</SubTitleText>
        </TitlesContainer>
        <BackButton data-test-id="addressDropdownBackBtn" onClick={() => onViewChange(AddressViewValuesMap.MAIN)} aria-label={t('back')}>
          <BackIcon />
        </BackButton>
      </BlockHeader>

      <FormWrapper
        {...{
          getStreetNameAutocomplete,
          onSubmit,
          handleDeleteButtonClick,
          fetchStreetNamesIfNeeded,
          fetchCityNamesIfNeeded,
          disabledDelete,
          setDisabledDelete,
          isCommentsExpended,
          setIsCommentsExpended,
          cityNameAutocomplete,
          streetNameAutocomplete,
          editing,
          editedAddress,
        }}
      />
    </>
  );
};

export default memo(EditRemoteAddress);
